import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useImportResumeSkills, useSkillProfile, useUpdateSkillProfile } from '@/hooks/use-learning'
import type { ContentFormat, SkillLevel, SkillProfileUpdate } from '@/types'
import { ArrowLeft, FileUp, Loader2, Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const SKILL_LEVELS: SkillLevel[] = ['beginner', 'intermediate', 'advanced', 'expert']
const CONTENT_FORMATS: { value: ContentFormat; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'audio', label: 'Audio' },
  { value: 'podcast', label: 'Podcast' },
]

interface SkillEntry {
  id: number
  name: string
  level: SkillLevel
}

function skillsToEntries(skills: Record<string, SkillLevel>, nextId: () => number): SkillEntry[] {
  return Object.entries(skills).map(([name, level]) => ({
    id: nextId(),
    name,
    level: level as SkillLevel,
  }))
}

function entriesToSkills(entries: SkillEntry[]): Record<string, SkillLevel> {
  const result: Record<string, SkillLevel> = {}
  for (const entry of entries) {
    const key = entry.name.trim()
    if (key) result[key] = entry.level
  }
  return result
}

function SkillEntries({
  label,
  skills,
  onChange,
}: {
  label: string
  skills: Record<string, SkillLevel>
  onChange: (skills: Record<string, SkillLevel>) => void
}) {
  const idCounter = useRef(0)
  const nextId = useCallback(() => ++idCounter.current, [])
  const [entries, setEntries] = useState<SkillEntry[]>(() => skillsToEntries(skills, nextId))

  // Track if we're making local edits to avoid sync loop
  const isLocalEdit = useRef(false)

  // Sync from parent when skills change externally (e.g. profile load, import)
  const prevSkillsRef = useRef(skills)
  useEffect(() => {
    if (prevSkillsRef.current !== skills && !isLocalEdit.current) {
      prevSkillsRef.current = skills
      setEntries(skillsToEntries(skills, nextId))
    }
    isLocalEdit.current = false
  }, [skills, nextId])

  const update = (newEntries: SkillEntry[]) => {
    isLocalEdit.current = true
    setEntries(newEntries)
    onChange(entriesToSkills(newEntries))
  }

  const addSkill = () => {
    update([...entries, { id: nextId(), name: '', level: 'beginner' }])
  }

  const removeSkill = (id: number) => {
    update(entries.filter((e) => e.id !== id))
  }

  const updateEntry = (id: number, name: string, level: SkillLevel) => {
    update(entries.map((e) => (e.id === id ? { ...e, name, level } : e)))
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button type="button" variant="outline" size="sm" onClick={addSkill}>
          <Plus className="h-3 w-3 mr-1" />
          Add
        </Button>
      </div>
      {entries.length === 0 && (
        <p className="text-sm text-muted-foreground">No skills added yet.</p>
      )}
      {entries.map((entry) => (
        <div key={entry.id} className="flex items-center gap-2">
          <Input
            placeholder="Skill name"
            value={entry.name}
            onChange={(e) => updateEntry(entry.id, e.target.value, entry.level)}
            className="flex-1"
          />
          <select
            value={entry.level}
            onChange={(e) => updateEntry(entry.id, entry.name, e.target.value as SkillLevel)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {SKILL_LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <Button type="button" variant="ghost" size="sm" onClick={() => removeSkill(entry.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  )
}

export function LearnProfilePage() {
  const navigate = useNavigate()
  const { data: profile, isLoading } = useSkillProfile()
  const updateProfile = useUpdateSkillProfile()
  const importSkills = useImportResumeSkills()

  const [targetRole, setTargetRole] = useState('')
  const [currentSkills, setCurrentSkills] = useState<Record<string, SkillLevel>>({})
  const [targetSkills, setTargetSkills] = useState<Record<string, SkillLevel>>({})
  const [experienceSummary, setExperienceSummary] = useState('')
  const [preferredFormat, setPreferredFormat] = useState<ContentFormat>('text')
  const [weeklyTimeGoal, setWeeklyTimeGoal] = useState(60)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [importError, setImportError] = useState<string | null>(null)

  useEffect(() => {
    if (profile) {
      setTargetRole(profile.target_role || '')
      setCurrentSkills((profile.current_skills as Record<string, SkillLevel>) || {})
      setTargetSkills((profile.target_skills as Record<string, SkillLevel>) || {})
      setExperienceSummary(profile.experience_summary || '')
      setPreferredFormat(profile.preferred_format || 'text')
      setWeeklyTimeGoal(profile.weekly_time_goal || 60)
    }
  }, [profile])

  const handleSave = async () => {
    setSaveError(null)
    const data: SkillProfileUpdate = {
      target_role: targetRole || null,
      current_skills: currentSkills,
      target_skills: targetSkills,
      experience_summary: experienceSummary || null,
      preferred_format: preferredFormat,
      weekly_time_goal: weeklyTimeGoal,
    }
    try {
      await updateProfile.mutateAsync(data)
      navigate('/learn')
    } catch {
      setSaveError('Failed to save profile. Please try again.')
    }
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full max-w-2xl mx-auto px-6 py-8">
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate('/learn')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Learn
        </Button>

        <h1 className="text-2xl font-bold mb-6">Learning Profile</h1>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Target Role</CardTitle>
              <CardDescription>What role are you working toward?</CardDescription>
            </CardHeader>
            <CardContent>
              <Input
                placeholder="e.g. Data Analyst, Product Manager"
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value)}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={importSkills.isPending}
              onClick={async () => {
                setImportError(null)
                try {
                  const result = await importSkills.mutateAsync()
                  setCurrentSkills(result.skills)
                  if (result.experience_summary) {
                    setExperienceSummary(result.experience_summary)
                  }
                } catch {
                  setImportError('Could not import skills. Make sure you have uploaded a resume.')
                }
              }}
            >
              {importSkills.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <FileUp className="h-4 w-4 mr-1" />
              )}
              Import from Resume
            </Button>
          </div>
          {importError && <p className="text-sm text-destructive">{importError}</p>}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Current Skills</CardTitle>
              <CardDescription>Skills you already have and your proficiency level</CardDescription>
            </CardHeader>
            <CardContent>
              <SkillEntries
                label="Your skills"
                skills={currentSkills}
                onChange={setCurrentSkills}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Target Skills</CardTitle>
              <CardDescription>Skills needed for your target role</CardDescription>
            </CardHeader>
            <CardContent>
              <SkillEntries
                label="Skills to learn"
                skills={targetSkills}
                onChange={setTargetSkills}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Learning Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Preferred Format</Label>
                <select
                  value={preferredFormat}
                  onChange={(e) => setPreferredFormat(e.target.value as ContentFormat)}
                  className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {CONTENT_FORMATS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Weekly Time Goal: {weeklyTimeGoal} minutes</Label>
                <input
                  type="range"
                  min={15}
                  max={300}
                  step={15}
                  value={weeklyTimeGoal}
                  onChange={(e) => setWeeklyTimeGoal(Number(e.target.value))}
                  className="mt-1 w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>15 min</span>
                  <span>5 hours</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {saveError && <p className="text-sm text-destructive">{saveError}</p>}
          <Button onClick={handleSave} disabled={updateProfile.isPending} className="w-full">
            {updateProfile.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Save Profile
          </Button>
        </div>
      </div>
    </div>
  )
}
